import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  notificationChannelsApi,
  type NotificationChannelChoice,
  type NotificationGroup,
} from "../../api/notifications";
import { useToast } from "../../components/Toast";

type Channel = "inApp" | "email";

/**
 * How the user is told of each event: in the app (the bell) and by e-mail. Only the events they can be told of are
 * listed, grouped as the portal's parts are, each with the channels the portal has for it; a change holds at once.
 */
export function NotificationChannelsCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = useState<NotificationChannelChoice[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    notificationChannelsApi.list().then(setRows).catch(() => setFailed(true));
  }, []);

  async function choose(row: NotificationChannelChoice, channel: Channel, on: boolean) {
    const put = (r: NotificationChannelChoice) =>
      setRows((all) => all?.map((x) => (x.event === r.event ? r : x)) ?? all);
    const change = channel === "inApp" ? { inApp: on } : { email: on };
    put({ ...row, ...change });
    try {
      put(await notificationChannelsApi.choose(row.event, change));
    } catch {
      put(row);
      toast.error(t("profile.notifications.failed", "The setting was not saved; try again."));
    }
  }

  const groups = rows ? [...new Set(rows.map((r) => r.group))] : [];
  const label = (r: NotificationChannelChoice) => t(`profile.notifications.event.${r.event}`, r.event);

  function cell(r: NotificationChannelChoice, channel: Channel) {
    const on = r[channel];
    const name = channel === "inApp"
      ? t("profile.notifications.inApp", "In the app")
      : t("profile.notifications.email", "By e-mail");
    return (
      <td style={center}>
        {on === null ? (
          <span style={{ color: "var(--ink-4)" }} title={t("profile.notifications.noChannel", "This event has no such channel.")}>—</span>
        ) : (
          <input type="checkbox" checked={on} aria-label={`${label(r)}: ${name}`}
            onChange={(e) => void choose(r, channel, e.target.checked)} />
        )}
      </td>
    );
  }

  return (
    <section style={card}>
      <h2 style={{ margin: 0, fontSize: 16, color: "var(--brand-ink)" }}>{t("profile.notifications.title", "Notifications")}</h2>
      <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
        {t("profile.notifications.hint", "For each event, choose how we tell you: in the app (the bell) and by e-mail. A change holds at once.")}{" "}
        {t("profile.notifications.newMail", "E-mail for events that used to only ring is off until you turn it on.")}
      </p>
      {failed ? (
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
          {t("profile.notifications.loadFailed", "The settings could not be read; try again later.")}
        </p>
      ) : !rows ? (
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>{t("profile.notifications.loading", "Loading…")}</p>
      ) : (
        <>
          {/* A table, not the block every table becomes on a phone: three narrow columns fit, and fill the card. */}
          <table className="tbl" style={{ display: "table", width: "100%" }}>
            <thead>
              <tr>
                <th>{t("profile.notifications.event", "Event")}</th>
                <th style={center}>{t("profile.notifications.inApp", "In the app")}</th>
                <th style={center}>{t("profile.notifications.email", "By e-mail")}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g: NotificationGroup) => [
                <tr key={g} className="band">
                  <td colSpan={3}>{t(`profile.notifications.group.${g}`, g)}</td>
                </tr>,
                ...rows.filter((r) => r.group === g).map((r) => (
                  <tr key={r.event}>
                    <td>{label(r)}</td>
                    {cell(r, "inApp")}
                    {cell(r, "email")}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
          {groups.includes("CHAT") && (
            <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 12 }}>
              {t("profile.notifications.chatNote", "The chat's e-mail comes for what rang in the app and stayed unread for an hour.")}
            </p>
          )}
        </>
      )}
    </section>
  );
}

const center: CSSProperties = { textAlign: "center", width: 110 };
// Matches the Profile page's own card style, as the appearance card does.
const card: CSSProperties = {
  background: "var(--surface)",
  padding: 20,
  borderRadius: 10,
  boxShadow: "var(--shadow-1)",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
